import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'

import {req} from '~/utils'

export type ExpiryPreview = {
  status: 'ready' | 'unavailable'
  asOf?: number
  windowEnd?: number
  refreshAt?: number
  policyVersion?: string
  items?: {
    kind: 'thirty_day' | 'signin'
    amount: number
    expiresAt: number
    pending?: boolean
  }[]
}

/** 激励广告状态，字段经 req 统一 camelize。 */
export type RewardedAdStatusResponse = {
  adSessionId: string
  cooldownUntil: number
  nextResetAt: number
  quota: number
  remainingCount: number
  rewardAmount: number
  serverTime: number
  errmsg?: string
}

/** 下一次服务端规则切换的等待时间，独立于预告金额是否为0。 */
export function getPreviewRefreshDelay(
  preview: ExpiryPreview,
  receivedAt: number,
  now: number
): number {
  return Math.max(
    0,
    (preview.refreshAt - preview.asOf) * 1000 - (now - receivedAt)
  )
}

function normalizeExpiryPreview(meta: any): ExpiryPreview {
  const items = meta?.items || []
  if (
    meta?.status !== 'ready' ||
    !Number.isSafeInteger(meta.asOf) ||
    meta.asOf <= 0 ||
    !Number.isSafeInteger(meta.windowEnd) ||
    meta.windowEnd <= meta.asOf ||
    !Number.isSafeInteger(meta.refreshAt) ||
    meta.refreshAt <= meta.asOf ||
    meta.refreshAt > meta.windowEnd ||
    !meta.policyVersion ||
    !Array.isArray(items) ||
    items.some(
      (item) =>
        !item ||
        !['thirty_day', 'signin'].includes(item.kind) ||
        !Number.isSafeInteger(item.amount) ||
        item.amount < 0 ||
        !Number.isSafeInteger(item.expiresAt) ||
        item.expiresAt <= 0 ||
        item.expiresAt > meta.windowEnd ||
        (item.expiresAt <= meta.asOf && !item.pending)
    ) ||
    !Number.isSafeInteger(items.reduce((sum, item) => sum + item.amount, 0))
  )
    return {status: 'unavailable'}
  return {...meta, items}
}

type LimitTask = {
  list: any
  title: string
  timestamp: string
}

type rechargeProps = {
  profileStatus: 'idle' | 'loading' | 'ready' | 'unavailable'
  profileHasValue: boolean
  profileRequestId: string | null
  profileReceivedAt: number
  expiryPreview: ExpiryPreview
  everyDayTask: any
  beginnerTask: any
  chargeList: any
  candyNumber: any
  signInData: any
  limitTask: LimitTask
}

const initialState = {
  profileStatus: 'idle',
  profileHasValue: false,
  profileRequestId: null,
  profileReceivedAt: 0,
  expiryPreview: {status: 'unavailable'},
  everyDayTask: null,
  beginnerTask: null,
  chargeList: null,
  candyNumber: {
    tokenBalance: 0,
    permanentTokenBalance: 0,
    temporaryTokenBalance: 0,
    expiringSoonTokenBalance: 0,
  },
  signInData: null,
  limitTask: {
    list: null,
    title: null,
    timestamp: null,
  },
} as rechargeProps

export const signInInfo = createAsyncThunk<any, any>(
  'signInInfo',
  async (arg, store) => {
    return req(
      '/task/get_sign_in_info',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const signIn = createAsyncThunk<any, any>(
  'signIn',
  async (arg, store) => {
    return req(
      '/task/activity_sign_in',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const limitTaskInfo = createAsyncThunk<any, any>(
  'limitTaskInfo',
  async (arg, store) => {
    return req(
      '/task/get_limited_task_info',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const dailyTaskInfo = createAsyncThunk<any, any>(
  'dailyTaskInfo',
  async (arg, store) => {
    return req(
      '/task/get_daily_task_info',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const newlyTaskInfo = createAsyncThunk<any, any>(
  'newlyTaskInfo',
  async (arg, store) => {
    return req(
      '/task/get_newly_task_info',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const productList = createAsyncThunk<any, any>(
  'productList',
  async (arg, store) => {
    return req(
      '/task/get_product_list',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const rewardedAdStatus = createAsyncThunk<RewardedAdStatusResponse, any>(
  'rewardedAdStatus',
  async (arg, store) => {
    return req(
      '/ad/rewarded/status',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

/** PingPong 收银台会话，字段经 req 统一 camelize */
export type PingPongSessionResponse = {
  errmsg?: string
  expireTs?: number
  jsSdkUrl?: string
  orderId?: string
  payAmount?: string
  payCurrency?: string
  sessionToken?: string
}

export type PingPongOrderStatusResponse = {
  errmsg?: string
  closeReason?: string
  payMethod?: 'ALIPAY' | 'WECHAT_PAY'
  /** 0 处理中 1 成功 2 失败 */
  status?: 0 | 1 | 2
}

export type JellyOrderStatus = 0 | 1 | 2 | 3 | 4

export type JellyOrder = {
  closeReason?: string
  commodityId: number
  createTs: number
  expireTs?: number
  jsSdkUrl?: string
  orderId: string
  payAmount: string
  payCurrency: string
  payMethod?: string
  payType: string
  refundStatus?: number
  sessionToken?: string
  status: JellyOrderStatus
  tokenCount: number
}

export type JellyOrderListResponse = {
  enabled?: boolean
  errmsg?: string
  list?: JellyOrder[]
  more?: 0 | 1
  offset?: number
}

export type CancelJellyOrderResponse = {
  cancelled?: boolean
  cancelStatus?: 'SUCCESS' | 'PENDING' | 'FAIL' | 'NOT_ALLOWED'
  closeReason?: string
  errmsg?: string
  orderId?: string
  status?: JellyOrderStatus
}

type CreateRechargeOrderParams = {
  commodity_id: number
  pay_type: 'pingpong'
  /** app：APK 内嵌 H5；web：官网。不传服务端按官网处理 */
  scene: 'app' | 'web'
}

type GetRechargeOrderStatusParams = {
  order_id: string
  pay_channel: 'pingpong'
}

type GetJellyOrderListParams = {
  limit: number
  offset: number
}

type CancelJellyOrderParams = {
  order_id: string
}

export const createRechargeOrder = createAsyncThunk<
  PingPongSessionResponse,
  CreateRechargeOrderParams
>('createRechargeOrder', async (arg, store) => {
  return req(
    '/pay/recharge_token_order',
    // @ts-ignore
    {...arg},
    store,
    false,
    null
  )
})

export const getRechargeOrderStatus = createAsyncThunk<
  PingPongOrderStatusResponse,
  GetRechargeOrderStatusParams
>('getRechargeOrderStatus', async (arg, store) => {
  return req(
    '/pay/get_order_status',
    // @ts-ignore
    {...arg},
    store,
    false,
    null
  )
})

export const getJellyOrderList = createAsyncThunk<
  JellyOrderListResponse,
  GetJellyOrderListParams
>('getJellyOrderList', async (arg, store) => {
  return req(
    '/pay/get_order_list',
    // @ts-ignore
    {...arg},
    store,
    false,
    null
  )
})

export const cancelJellyOrder = createAsyncThunk<
  CancelJellyOrderResponse,
  CancelJellyOrderParams
>('cancelJellyOrder', async (arg, store) => {
  return req(
    '/pay/cancel_order',
    // @ts-ignore
    {...arg},
    store,
    false,
    null
  )
})

export const accquireLimitReward = createAsyncThunk<any, any>(
  'accquireLimitReward',
  async (arg, store) => {
    return req(
      '/task/acquire_limited_task_reward',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const accquireDailyReward = createAsyncThunk<any, any>(
  'accquireDailyReward',
  async (arg, store) => {
    return req(
      '/task/acquire_daily_task_reward',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const accquireNewlyReward = createAsyncThunk<any, any>(
  'accquireNewlyReward',
  async (arg, store) => {
    return req(
      '/task/acquire_newly_task_reward',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const userProfile = createAsyncThunk<any, any>(
  'userProfile',
  async (arg, store) => {
    return req(
      '/task/get_user_game_info',
      // @ts-ignore
      {...arg},
      store,
      false,
      null
    )
  }
)

export const updateLimitTask = createAsyncThunk<any, any>(
  'updateLimitTask',
  async (arg, store) => {
    return arg
  }
)

export const updateEveryDayTask = createAsyncThunk<any, any>(
  'updateEveryDayTask',
  async (arg, store) => {
    return arg
  }
)

export const updateBeginnerTask = createAsyncThunk<any, any>(
  'updateBeginnerTask',
  async (arg, store) => {
    return arg
  }
)

export const reportSlice = createSlice({
  name: 'recharge',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(signInInfo.fulfilled, (state, action) => {
      return {
        ...state,
        signInData: action.payload,
      }
    })
    builder.addCase(signIn.fulfilled, (state, action) => {
      if (!action.payload || action.payload.errmsg) {
        return state
      }
      return {
        ...state,
        signInData: action.payload,
      }
    })
    builder.addCase(limitTaskInfo.fulfilled, (state, action) => {
      return {
        ...state,
        limitTask: action.payload,
      }
    })
    builder.addCase(dailyTaskInfo.fulfilled, (state, action) => {
      return {
        ...state,
        everyDayTask: action.payload.list,
      }
    })
    builder.addCase(newlyTaskInfo.fulfilled, (state, action) => {
      return {
        ...state,
        beginnerTask: action.payload.list,
      }
    })
    builder.addCase(productList.fulfilled, (state, action) => {
      return {
        ...state,
        chargeList: action.payload.list,
      }
    })
    builder.addCase(userProfile.pending, (state, action) => {
      state.profileRequestId = action.meta.requestId
      state.profileStatus = 'loading'
      state.expiryPreview = {status: 'unavailable'}
    })
    builder.addCase(userProfile.fulfilled, (state, action) => {
      if (state.profileRequestId !== action.meta.requestId) return
      const {payload} = action
      state.profileRequestId = null
      const balanceKeys = [
        'tokenBalance',
        'temporaryTokenBalance',
        'permanentTokenBalance',
        'expiringSoonTokenBalance',
      ]
      if (
        !payload ||
        payload.errmsg ||
        balanceKeys.some(
          (key) => !Number.isSafeInteger(payload[key]) || payload[key] < 0
        )
      ) {
        state.profileStatus = 'unavailable'
        state.expiryPreview = {status: 'unavailable'}
        return
      }
      state.profileStatus = 'ready'
      state.profileHasValue = true
      state.profileReceivedAt = Date.now()
      state.candyNumber = payload
      state.expiryPreview = normalizeExpiryPreview(payload.expiryPreview)
    })
    builder.addCase(userProfile.rejected, (state, action) => {
      if (state.profileRequestId !== action.meta.requestId) return
      state.profileRequestId = null
      state.profileStatus = 'unavailable'
      state.expiryPreview = {status: 'unavailable'}
    })
    builder.addCase(updateLimitTask.fulfilled, (state, action) => {
      return {
        ...state,
        limitTask: {
          ...state.limitTask,
          list: action.payload.list,
        },
      }
    })
    builder.addCase(updateEveryDayTask.fulfilled, (state, action) => {
      return {
        ...state,
        everyDayTask: action.payload.list,
      }
    })
    builder.addCase(updateBeginnerTask.fulfilled, (state, action) => {
      return {
        ...state,
        beginnerTask: action.payload.list,
      }
    })
  },
})

export default reportSlice.reducer
